/**
 * StrategyVersionService — immutable version tree for strategies (Spec A / D51).
 *
 * Closes the one mutable link in the experiment pin-chain. A strategy's working
 * head is mutable while `status='draft'`; an explicit **Bake** snapshots the head
 * (blocks + screens + variables/tasks + theme) into an immutable version under
 *   projects/{projectId}/strategies/{sid}/versions/{versionId}
 * with block/screen SUBCOLLECTIONS mirroring the head (so CLI + canvas can read a
 * version exactly like the head — see A7-2, A6). Versions form a tree via
 * `parentVersion`, exactly like onboarding versions.
 *
 * Anti-spam contract (Q4): repeated `draft` edits mint NO version. A version is
 * born ONLY at Bake. A baked/published strategy is read-only until the operator
 * explicitly starts a new version.
 *
 * Sibling patterns: VariableVersionService (append-only versions + diff),
 * OnboardingService.createVersion (version metadata doc + latest pointer).
 */

import {
  doc,
  collection,
  getDoc,
  getDocs,
  updateDoc,
  query,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import type {
  Strategy,
  StrategyVersion,
  StrategyInputManifest,
} from '@segmently/schema-models/strategy';
import type { StepNode } from '@segmently/schema-models/v2';
import { restoreStrategyViaApi } from '@modules/firebase-core/services/StrategyRestoreApi';
import { strategyHeadComparableJson } from '@modules/firebase-core/services/StrategyHeadMutationApi';
import { canonicalStrategyVersionContentJson, type CliStrategyRestoreData } from '@modules/shared-types/src/strategyVersionContent';
import { CoreFirebaseService } from './CoreFirebaseService';
import { getBlockLibraryCliApiBaseUrl } from '@modules/firebase-core/services/BlockLibraryMutationApi';
import type { OnboardingThemeSettingsV2 } from '../converters/onboardingConverter';
import { StrategyBlockService } from './StrategyBlockService';
import { StrategyStepNodeService } from './StrategyStepNodeService';
// Citation grammar has ONE client-side source (data-pickers pathParser) —
// subpath import keeps React out of this service's import graph.
import { parsePath } from '@modules/data-pickers/shared/pathParser';

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

export interface BakeStrategyOptions {
  /** Human-readable label for the version (defaults to "v{N}"). */
  name?: string;
  /** U8 (Spec D2): operator note on what changed in this version. */
  description?: string;
  /** Compatibility hint; the authenticated backend identity owns the audit UID. */
  bakedByUid?: string;
  /** Reviewed lockfile: when supplied it must match the saved head; never overrides it. */
  inputManifest?: StrategyInputManifest;
  /** Bake even if the strategy is already baked (default false → idempotent no-op). */
  force?: boolean;
}

/** Reconstructed content of a version (for view / diff / cherry-pick — A6). */
export interface StrategyVersionSnapshot {
  version: StrategyVersion;
  /** Snapshot of the head content fields (name, variables, tasks, …) — excludes lifecycle/system fields. */
  head: Partial<Strategy>;
  /** Block documents (each carries its Firestore `id`). */
  blocks: Array<Record<string, unknown> & { id: string }>;
  /** blockId → its StepNode screens. */
  screensByBlock: Record<string, StepNode[]>;
  /** Theme snapshot, or null if the strategy had no theme. */
  theme: OnboardingThemeSettingsV2 | null;
}

const VERSIONS_SUBCOLLECTION = 'versions';
const THEME_SUBCOLLECTION = 'themeSettingsV2';
const THEME_DOC_ID = 'settings';

// ═══════════════════════════════════════════════════════
// PURE HELPERS
// ═══════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════
// SERVICE
// ═══════════════════════════════════════════════════════

export class StrategyVersionService {
  private readonly firestore = CoreFirebaseService.getInstance().firestore;

  constructor(private readonly projectId: string) {}

  private strategyPath(strategyId: string): string {
    return `projects/${this.projectId}/strategies/${strategyId}`;
  }

  private versionsPath(strategyId: string): string {
    return `${this.strategyPath(strategyId)}/${VERSIONS_SUBCOLLECTION}`;
  }

  // ── Reads ───────────────────────────────────────────

  /** List all versions, newest first (by bakedAt). */
  async listVersions(strategyId: string): Promise<StrategyVersion[]> {
    try {
      const col = collection(this.firestore, this.versionsPath(strategyId));
      const snap = await getDocs(query(col, orderBy('bakedAt', 'desc')));
      return snap.docs.map((d) => {
        const data = d.data() as Record<string, unknown>;
        // The stored doc also carries a `head` blob; strip it from the metadata view.
        const { head: _head, ...meta } = data;
        return { ...meta, versionId: d.id } as StrategyVersion;
      });
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StrategyVersionService.listVersions');
      throw error;
    }
  }

  /** Get a single version's metadata (without the full content snapshot). */
  async getVersion(strategyId: string, versionId: string): Promise<StrategyVersion | null> {
    const ref = doc(this.firestore, this.versionsPath(strategyId), versionId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    const { head: _head, ...meta } = snap.data() as Record<string, unknown>;
    return { ...meta, versionId: snap.id } as StrategyVersion;
  }

  /**
   * Reconstruct a version's full content (head fields + blocks + screens + theme).
   * Powers read-only version viewing, version diff, and cherry-pick (A6).
   */
  async getVersionSnapshot(strategyId: string, versionId: string): Promise<StrategyVersionSnapshot | null> {
    const versionRef = doc(this.firestore, this.versionsPath(strategyId), versionId);
    const versionSnap = await getDoc(versionRef);
    if (!versionSnap.exists()) return null;
    const raw = versionSnap.data() as Record<string, unknown>;
    const { head, ...meta } = raw;
    const version = { ...meta, versionId: versionSnap.id } as StrategyVersion;

    const blocksCol = collection(this.firestore, `${this.versionsPath(strategyId)}/${versionId}/blocks`);
    const blocksSnap = await getDocs(blocksCol);
    const blocks: Array<Record<string, unknown> & { id: string }> = [];
    const screensByBlock: Record<string, StepNode[]> = {};
    for (const blockDoc of blocksSnap.docs) {
      blocks.push({ ...(blockDoc.data() as Record<string, unknown>), id: blockDoc.id });
      const screensCol = collection(
        this.firestore,
        `${this.versionsPath(strategyId)}/${versionId}/blocks/${blockDoc.id}/screens`,
      );
      const screensSnap = await getDocs(screensCol);
      screensByBlock[blockDoc.id] = screensSnap.docs.map(
        (s) => ({ ...(s.data() as Record<string, unknown>), id: s.id }) as unknown as StepNode,
      );
    }

    const themeRef = doc(
      this.firestore,
      `${this.versionsPath(strategyId)}/${versionId}/${THEME_SUBCOLLECTION}`,
      THEME_DOC_ID,
    );
    const themeSnap = await getDoc(themeRef);
    const theme = themeSnap.exists() ? (themeSnap.data() as OnboardingThemeSettingsV2) : null;

    return { version, head: (head as Partial<Strategy>) ?? {}, blocks, screensByBlock, theme };
  }

  // ── Bake (mint an immutable version) ────────────────

  /**
   * Snapshot the working head into a new immutable version and flip the strategy
   * to `baked`. Idempotent: a strategy that is already baked (and unchanged, since
   * a baked strategy is read-only) is a no-op that returns the current version.
   */
  async bake(strategyId: string, opts: BakeStrategyOptions = {}): Promise<StrategyVersion> {
    const request = JSON.stringify({ name: opts.name, description: opts.description,
      force: opts.force, expectedInputManifest: opts.inputManifest });
    try {
      const user = CoreFirebaseService.getInstance().auth.currentUser;
      if (!user) throw new Error('Sign in before baking a strategy.');
      const base = getBlockLibraryCliApiBaseUrl().replace(/\/$/, '');
      const capabilityResponse = await fetch(`${base}/api/cli/v1/capabilities`);
      const capabilities = await capabilityResponse.json();
      if (!capabilityResponse.ok || capabilities?.success === false || capabilities?.data?.features?.transactionalStrategyBake !== true) {
        throw new Error('The server does not support protected strategy Bake yet. Update the server before retrying.');
      }
      const token = await user.getIdToken();
      const response = await fetch(`${base}/api/cli/v1/projects/${encodeURIComponent(this.projectId)}/strategies/${encodeURIComponent(strategyId)}/bake`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: request,
      });
      const payload = await response.json();
      if (!response.ok || payload?.success === false) {
        throw new Error(payload?.error?.message ?? payload?.detail ?? 'Strategy Bake failed.');
      }
      const data = payload?.data;
      if (data?.projectId !== this.projectId || data?.strategyId !== strategyId
        || typeof data?.versionId !== 'string' || !data.versionId || data.versionId.includes('/')
        || data.status !== 'baked' || typeof data.contentHash !== 'string' || !data.contentHash) {
        throw new Error('Strategy Bake returned an invalid receipt. Inspect the current strategy before retrying.');
      }
      const version = await this.getVersion(strategyId, data.versionId);
      if (!version || version.contentHash !== data.contentHash || version.blockCount !== data.blockCount
        || version.screenCount !== data.screenCount || version.parentVersion !== data.parentVersion) {
        throw new Error(`Strategy version ${data.versionId} was returned, but its persisted receipt could not be verified. Refresh before retrying.`);
      }
      return version;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StrategyVersionService.bake');
      throw error;
    }
  }

  // ── Start a new (draft) version ─────────────────────

  /**
   * Reopen the strategy for editing. From the latest baked node this just flips
   * `status` back to `draft` (the head already equals `currentVersion`). Branching
   * from a NON-latest node (`fromVersionId`) restores that snapshot into the head
   * and repoints `currentVersion` so the NEXT bake's `parentVersion` is that node
   * (the version tree — AC-A2-6).
   */
  async startNewVersion(strategyId: string, fromVersionId?: string): Promise<void> {
    try {
      const receipt = await restoreStrategyViaApi(this.projectId, strategyId, fromVersionId);
      await this.verifyRestoredHead(strategyId, receipt);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StrategyVersionService.startNewVersion');
      throw error;
    }
  }

  /**
   * Cherry-pick a single block (with its screens) from a version into the working
   * head as a NEW block, and flip the head to `draft` (A6-4). Other head blocks are
   * untouched.
   */
  async cherryPickBlock(strategyId: string, fromVersionId: string, sourceBlockId: string): Promise<string> {
    const snapshot = await this.getVersionSnapshot(strategyId, fromVersionId);
    if (!snapshot) throw new Error(`Version ${fromVersionId} not found`);
    const sourceBlock = snapshot.blocks.find((b) => b.id === sourceBlockId);
    if (!sourceBlock) throw new Error(`Block ${sourceBlockId} not in version ${fromVersionId}`);

    // Spec D4 (U11, decision 3b): the copied screens keep their old research
    // citations — stamp the denormalized ids + provenance so the canvas can
    // flag "cites old analysis" without loading screens.
    const sourceScreens = snapshot.screensByBlock[sourceBlockId] ?? [];
    const citedAnalysisIds = [...new Set(sourceScreens.flatMap((screen) => {
      const dataMapping = (screen as { aiMeta?: { dataMapping?: Array<{ sourcePath?: string }> } }).aiMeta?.dataMapping;
      return (Array.isArray(dataMapping) ? dataMapping : []).flatMap((mapping) => {
        if (typeof mapping?.sourcePath !== 'string') return [];
        const parsed = parsePath(mapping.sourcePath);
        return parsed?.entityType === 'insightsAnalysis' && parsed.entityId ? [parsed.entityId] : [];
      });
    }))].sort();

    const blockService = new StrategyBlockService(this.projectId, strategyId);
    const { id: _oldId, ...blockData } = sourceBlock;
    const created = await blockService.create({
      ...(blockData as Record<string, unknown>),
      name: `${(blockData as { name?: string }).name ?? 'Block'} (from ${snapshot.version.name ?? fromVersionId})`,
      entityId: crypto.randomUUID(),
      action: { ...(blockData as { action?: Record<string, unknown> }).action, edges: [] },
      citedAnalysisIds,
      cherryPickedFrom: { versionId: fromVersionId, sourceBlockId },
    } as never);

    const newBlockId = (created as { id: string }).id;
    const stepService = new StrategyStepNodeService(this.projectId, strategyId, newBlockId);
    for (const screen of snapshot.screensByBlock[sourceBlockId] ?? []) {
      await stepService.addScreen(screen);
    }

    const strategyRef = doc(this.firestore, this.strategyPath(strategyId));
    await updateDoc(strategyRef, { status: 'draft', updatedAt: Timestamp.now() });
    return newBlockId;
  }

  /** Verify the complete working content after the server's atomic restore. */
  private async verifyRestoredHead(strategyId: string, receipt: CliStrategyRestoreData): Promise<void> {
    const root = this.strategyPath(strategyId);
    const head = await getDoc(doc(this.firestore, root));
    if (!head.exists()) throw new Error(`Strategy ${strategyId} could not be read after restore. Refresh before retrying.`);
    const blocks = await getDocs(collection(this.firestore, `${root}/blocks`));
    const bundles = [];
    for (const block of blocks.docs) {
      const screens = await getDocs(collection(this.firestore, `${root}/blocks/${block.id}/screens`));
      bundles.push({ block: { ...block.data(), id: block.id }, screens: screens.docs.map(screen => ({ ...screen.data(), id: screen.id })) });
    }
    const theme = await getDoc(doc(this.firestore, `${root}/${THEME_SUBCOLLECTION}/${THEME_DOC_ID}`));
    const content = JSON.parse(strategyHeadComparableJson({ head: head.data(), bundles, theme: theme.exists() ? theme.data() : null }));
    const canonical = canonicalStrategyVersionContentJson(content);
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(canonical));
    const actualHash = [...new Uint8Array(digest)].map(byte => byte.toString(16).padStart(2, '0')).join('');
    if (head.data().status !== 'draft' || (head.data().currentVersion ?? '') !== receipt.currentVersion
      || actualHash !== receipt.contentHash || blocks.size !== receipt.blockCount
      || bundles.reduce((count, bundle) => count + bundle.screens.length, 0) !== receipt.screenCount) {
      throw new Error(`Strategy ${strategyId} persisted restore differs from its receipt. Refresh before retrying.`);
    }
  }
}
